import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import logo from '../../assets/icons/cm_logo.svg';
import styles from './Navbar.module.css';
import Config from '../../utils/Config';
import { GoogleOAuthProvider } from '@react-oauth/google';
import GoogleAuth from '../Auth/Google/GoogleAuth';
import ProjectIcon from '@mui/icons-material/Store';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MenuIcon from '@mui/icons-material/Menu';
import getCMResponse from '../../utils/api_manager';
import { useCurrentProjectWriter } from '../../utils/redux/currentProjectService';
import { consumeForShopifyReturn } from '../../utils/shopifyGrantFlowProject';
import AppLink from '../AppLink/AppLink';

const Navbar = (props) => {
  const {
    currentUser,
    initUser,
    setUser,
    currentProject,
    projects,
    pendingInvitations,
    onSwitchProject,
    onMenuClick,
  } = props;

  const isLoggedIn = currentUser && Object.keys(currentUser).length > 0;
  const hasProject = (projects || []).length > 0;

  // ---- Logged-in (states 2/3) hooks/state — must run unconditionally ----
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCurrentProject } = useCurrentProjectWriter();
  const isCreateProjectRoute = location.pathname === '/create-project';
  const projectName = isCreateProjectRoute
    ? 'Create Project'
    : (currentProject?.name || 'Main Project');

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const avatarRef = useRef(null);
  const projectRef = useRef(null);

  const projectList = projects || [];
  const invitationList = pendingInvitations || [];
  const showProjectSwitcher = hasProject;

  const handleProjectToggle = useCallback(() => {
    setProjectMenuOpen(prev => !prev);
  }, []);

  const handleSelectProject = useCallback((project) => {
    setProjectMenuOpen(false);
    if (project.id === currentProject?.id && !isCreateProjectRoute) return;
    if (onSwitchProject) onSwitchProject(project);
  }, [currentProject, onSwitchProject, isCreateProjectRoute]);

  // link-standard-navigation-behavior-trd.md Section 2.1 R2: these two are
  // now real AppLinks (left-click SPA nav, right-click/ctrl/cmd/middle-click
  // native anchor behavior). Closing the dropdown on click is preserved via
  // AppLink's onNavigate side-effect prop.
  const handleSelectInvitationNavigate = useCallback(() => {
    setProjectMenuOpen(false);
  }, []);

  const handleCreateProjectNavigate = useCallback(() => {
    setProjectMenuOpen(false);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!projectMenuOpen) return;

    const handleOutsideClick = (e) => {
      if (projectRef.current && !projectRef.current.contains(e.target)) {
        setProjectMenuOpen(false);
      }
    };

    const tid = setTimeout(() => {
      document.addEventListener('mousedown', handleOutsideClick);
    }, 0);

    return () => {
      clearTimeout(tid);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [projectMenuOpen]);

  const firstName = (currentUser?.name || '').split(' ')[0] || '';
  const initial = firstName.charAt(0).toUpperCase() || '?';
  const picture = currentUser?.picture || null;

  const handleAvatarClick = useCallback(() => {
    setDropdownOpen(prev => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    getCMResponse('/auth/logout', 'POST').then(() => {
      if (setUser) setUser({});
      clearCurrentProject();
      // connect-cta-new-tab-project-leak-trd.md Section 3, R9: clear any
      // lingering cm_current_project key left behind by an abandoned
      // mid-flow Shopify grant (started, never returned to consume it) --
      // defense-in-depth so a different user logging into the same tab
      // afterward can't have it available to LegacyProjectRedirect's
      // next consume.
      consumeForShopifyReturn();
      navigate('/');
    }).catch(() => {
      if (setUser) setUser({});
      clearCurrentProject();
      consumeForShopifyReturn();
      navigate('/');
    });
  }, [navigate, setUser, clearCurrentProject]);

  // Close on outside click
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleOutsideClick = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };

    const tid = setTimeout(() => {
      document.addEventListener('mousedown', handleOutsideClick);
    }, 0);

    return () => {
      clearTimeout(tid);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [dropdownOpen]);

  // ---- State 1: logged out ----
  if (!isLoggedIn) {
    return (
      <div className={styles.nav_outer}>
        <nav className={styles.navbar}>
          <Link to="/" className={styles.navbar_brand}>
            <img src={logo} alt="Company Logo" />
            <h1>Cliqmetrics</h1>
          </Link>
          <div className={styles.nav_icons_container}>
            {Config.GOOGLE_AUTH_ID
              ? (
                <GoogleOAuthProvider clientId={Config.GOOGLE_AUTH_ID}>
                  <GoogleAuth onLogin={initUser} variant="branded" />
                </GoogleOAuthProvider>
              )
              : <button disabled className={styles.button}>Login with Google</button>
            }
          </div>
        </nav>
      </div>
    );
  }

  // ---- States 2/3: logged in (no project / has project) ----
  return (
    <nav className={styles.topNav}>
      <div className={styles.leftSection}>
        <button className={styles.mobileMenuButton} onClick={onMenuClick} aria-label="Toggle menu">
          <MenuIcon />
        </button>
        <div className={styles.logoSection}>
          <img
            src={logo}
            alt="Cliqmetrics Logo"
            className={styles.logoImg}
          />
          <span className={styles.logoText}>Cliqmetrics</span>
        </div>
        {showProjectSwitcher && (
          <div ref={projectRef} className={styles.projectWrapper}>
            <button className={styles.projectSelector} onClick={handleProjectToggle}>
              <ProjectIcon className={styles.projectIcon} />
              <span className={styles.projectName}>{projectName}</span>
              <ArrowDropDownIcon />
            </button>

            {projectMenuOpen && (
              <div className={styles.projectDropdown}>
                {projectList.map(sf => (
                  <button
                    key={sf.id}
                    className={`${styles.projectDropdownItem} ${sf.id === currentProject?.id ? styles.projectDropdownItemActive : ''}`}
                    onClick={() => handleSelectProject(sf)}
                  >
                    <ProjectIcon className={styles.projectDropdownIcon} />
                    <span className={styles.projectDropdownName}>{sf.name}</span>
                  </button>
                ))}

                {invitationList.length > 0 && (
                  <>
                    <div className={styles.projectDropdownDivider} />
                    {invitationList.map(inv => (
                      <AppLink
                        key={inv.id}
                        className={styles.projectDropdownItem}
                        to="/accept-invite"
                        onNavigate={handleSelectInvitationNavigate}
                      >
                        <ProjectIcon className={styles.projectDropdownIcon} />
                        <span className={styles.projectDropdownName}>{inv.project_name || inv.email}</span>
                        <span className={styles.pendingBadge}>Pending</span>
                      </AppLink>
                    ))}
                  </>
                )}

                <div className={styles.projectDropdownDivider} />
                <AppLink
                  className={styles.projectDropdownCreate}
                  to="/create-project"
                  onNavigate={handleCreateProjectNavigate}
                >
                  + Create new project
                </AppLink>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.rightSection}>
        <div ref={avatarRef} className={styles.avatarWrapper}>
          {picture ? (
            <img
              src={picture}
              alt={firstName || 'User'}
              referrerPolicy="no-referrer"
              className={styles.avatarImg}
              onClick={handleAvatarClick}
            />
          ) : (
            <div
              className={styles.avatarInitial}
              onClick={handleAvatarClick}
              aria-label="User menu"
            >
              {initial}
            </div>
          )}

          {dropdownOpen && (
            <div className={styles.dropdown}>
              <button className={styles.dropdownItem} onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
